import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl, LayerGroup } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import { Maximize2, Minimize2 } from 'lucide-react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { getMeasuringBoxesMaps } from './core/_requests'
import { ID, QUERIES } from '../../../_metronic/helpers'
import { useAuth } from '../../modules/auth'
import { useAlarmedBoxes } from '../sensors-v2/hooks/useAlarmedBoxes'
import { maxSeverity } from '../../components/box-status-card/severity'
import { isBoxOnline } from '../../components/box-status-card/presenceStatus'
import { SensorTransportProvider } from '../sensors-v2/transports/SensorTransportProvider'
import { subscribePresenceChanged } from '../sensors-v2/transports/WebSocketTransport'
import { PageCard, SectionLabel, StatBlock, TOKENS } from '../../components/design-system'
import {
  MeasuringBoxModels,
  MeasuringBoxModelsList,
} from '../../modules/apps/measuring-box-management/measuring-box-list/core/_models'
import { EditModal } from '../../modules/apps/measuring-box-management/measuring-box-list/measuring-boxes-edit-modal/EditModal'
import { useListView } from '../../modules/apps/measuring-box-management/measuring-box-list/core/ListViewProvider'

type Status = 'high' | 'medium' | 'ok' | 'offline'

const STATUS_COLOR: Record<Status, string> = {
  high: TOKENS.color.severity.high.text,
  medium: TOKENS.color.severity.medium.text,
  ok: TOKENS.color.severity.low.text,
  offline: TOKENS.color.muted,
}

const STATUS_LABEL: Record<Status, string> = {
  high: 'Alta',
  medium: 'Média',
  ok: 'OK',
  offline: 'Offline',
}

// Box icon (matches lucide-react's Box) rendered inline so it can live
// inside a Leaflet DivIcon's HTML string. Color comes through via the
// `stroke` attribute set when we build the marker.
function boxSvg(color: string): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
         fill="none" stroke="${color}" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  `
}

// Status precedence used by the cluster icon: severity wins, OK over offline.
// Lower score = more concerning. Drives the cluster bubble's color
// when child markers have different statuses. Note: offline ranks
// ABOVE ok (i.e. takes precedence over green) because an offline
// box is a signal that something needs attention — a cluster of
// "1 ok + 1 offline" should NOT be painted green, that hides the
// real story. Operationally:
//   high  → active alarm           (worst, red)
//   medium → moderate severity     (orange)
//   offline → unreachable          (gray — needs attention)
//   ok    → healthy                (best, green)
const CLUSTER_PRIORITY: Record<Status, number> = { high: 0, medium: 1, offline: 2, ok: 3 }

// Worst-status of any child marker inside a cluster, used to color the
// cluster bubble. Reads the per-marker status we stamp on the Leaflet
// marker via its `options.title` (we encode "<status>" there because
// react-leaflet-cluster doesn't pass arbitrary props through).
function clusterWorstStatus(cluster: L.MarkerCluster): Status {
  const children = cluster.getAllChildMarkers()
  // Start unset so any child status wins — fixes a separate latent bug
  // where a cluster of all-OK boxes would stay colored as the initial
  // seed because OK never beat the seed's score.
  let worst: Status | null = null
  let worstScore = Infinity
  for (const child of children) {
    const status = (child.options.title as Status) ?? 'offline'
    const score = CLUSTER_PRIORITY[status] ?? 99
    if (score < worstScore) { worst = status; worstScore = score }
  }
  return worst ?? 'offline'
}

function buildClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const count = cluster.getChildCount()
  const status = clusterWorstStatus(cluster)
  const color = STATUS_COLOR[status]
  const size = count < 10 ? 38 : count < 100 ? 44 : 52
  const html = `
    <div style="
      width:${size}px;
      height:${size}px;
      border-radius:50%;
      background:${color};
      color:#fff;
      display:flex;
      align-items:center;
      justify-content:center;
      font-weight:700;
      font-size:13px;
      border:3px solid #fff;
      box-shadow:0 2px 8px rgba(17,24,39,0.25);
    ">${count}</div>
  `
  return L.divIcon({
    html,
    className: 'cmb-cluster-icon',
    iconSize: L.point(size, size, true),
  })
}

// Token-driven Leaflet DivIcon — a rounded square holding the Box icon
// and the CMB number underneath. Color reflects the box's current alarm
// status. Replaces the plain colored circle that was the first cut.
function buildMarkerIcon(cmbNumber: number | string, status: Status): L.DivIcon {
  const color = STATUS_COLOR[status]
  const label = String(cmbNumber ?? '?')
  const html = `
    <div style="
      position:relative;
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      width:50px;
      font-family:inherit;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.18));
    ">
      <div style="
        width:34px;height:34px;
        background:#ffffff;border-radius:8px;
        border:2px solid ${color};
        display:flex;align-items:center;justify-content:center;
      ">
        ${boxSvg(color)}
      </div>
      <div style="
        margin-top:3px;
        background:${color};color:#fff;
        font-size:10px;font-weight:700;letter-spacing:0.2px;
        padding:1px 6px;border-radius:6px;
        line-height:1.3;
      ">${label}</div>
    </div>
  `
  return L.divIcon({
    html,
    className: '',
    iconSize: [50, 56],
    iconAnchor: [25, 28],
    popupAnchor: [0, -28],
  })
}

const Recenter: FC<{ position: [number, number] | null; zoom?: number }> = ({ position, zoom }) => {
  const map = useMap()
  useEffect(() => {
    if (position) map.setView(position, zoom ?? map.getZoom())
  }, [position, zoom, map])
  return null
}

/**
 * Frames the map at Brazil-wide view: Brasília centroid + zoom 4 —
 * covers from Roraima to Rio Grande do Sul comfortably. Operator sees
 * the whole country at a glance and decides where to zoom in.
 *
 * Two triggers:
 *   - Initial mount (lastResetKey null → first effect run resets)
 *   - Company change — when resetKey changes, view returns to
 *     Brazil-wide so the operator gets a clean context reset between
 *     companies instead of being stranded on the previous one's view.
 *
 * Filter / search re-renders don't fire (key unchanged). Click-to-zoom
 * on a marker still works via Recenter, independent of this effect.
 */
const BRAZIL_CENTER: [number, number] = [-15.78, -47.92]
const BRAZIL_ZOOM = 4

const ResetToBrazilOnKey: FC<{ resetKey: string | number }> = ({ resetKey }) => {
  const map = useMap()
  const lastResetKey = useRef<string | number | null>(null)
  useEffect(() => {
    if (lastResetKey.current === resetKey) return
    map.setView(BRAZIL_CENTER, BRAZIL_ZOOM)
    lastResetKey.current = resetKey
  }, [resetKey, map])
  return null
}

const InvalidateOnResize: FC<{ trigger: unknown }> = ({ trigger }) => {
  const map = useMap()
  useEffect(() => {
    // After a layout change (fullscreen toggle, sidebar collapse) the map
    // tiles need to be refetched against the new container size.
    const t = setTimeout(() => map.invalidateSize(), 200)
    return () => clearTimeout(t)
  }, [trigger, map])
  return null
}

const LeafletMapInner: FC = () => {
  const { itemIdForUpdate, setItemIdForUpdate, setReadOnly } = useListView()
  const { currentUser } = useAuth()
  const companyId = currentUser?.last_company_id ? +currentUser.last_company_id : 0

  const { data: measuringBoxesMaps, isFetching } = useQuery(
    // Bug fix: the previous key was static so changing the company in the
    // header didn't refresh the markers. Include company_id so each company
    // gets its own cache entry.
    [`${QUERIES.MEASURING_BOXES_LIST}-MAP`, companyId],
    async () => {
      const res = await getMeasuringBoxesMaps({ company_id: companyId, take: 0, skip: 10000 })
      // Used to filter `box.lat !== null` here, which made the map
      // panel + totals disagree with the dashboard hero (dashboard
      // counts every box, map ignored ungeocoded ones). Keep the
      // full list — the map markers themselves only render where lat
      // exists (filter at render time below), but the side list and
      // status totals include everyone.
      return res.data ?? []
    },
    {
      enabled: companyId > 0,
      // Polling is the safety net — the realtime path is the WS
      // presence_changed subscription below, which invalidates this
      // query within seconds of a heartbeat landing. 60s covers tabs
      // that lose the WS without us noticing.
      refetchInterval: 60_000,
      refetchOnWindowFocus: false,
      onError: (err: any) => {
        console.error(err)
        toast.error(`Erro ao carregar as caixas no mapa: ${err.message}`)
      },
    },
  )

  // Realtime: gateway broadcasts presence_changed on every heartbeat,
  // we re-fetch the box list so last_seen_at is current and any box
  // that just resumed heartbeating flips from offline → ok within a
  // second instead of waiting on the safety-net poll.
  const queryClient = useQueryClient()
  useEffect(() => {
    return subscribePresenceChanged(() => {
      queryClient.invalidateQueries([`${QUERIES.MEASURING_BOXES_LIST}-MAP`])
    })
  }, [queryClient])

  const { data: alarmedData } = useAlarmedBoxes({
    sort: 'gravity_desc',
    page: 1,
    items_per_page: 1000,
    company_id: companyId,
  })

  const severityByBoxId = useMemo(() => {
    const m = new Map<number, Status>()
    for (const b of alarmedData?.data ?? []) {
      if (!b.active_alarms.length) continue
      const sev = maxSeverity(b.active_alarms)
      m.set(Number(b.box_id), sev === 'high' ? 'high' : sev === 'medium' ? 'medium' : 'ok')
    }
    return m
  }, [alarmedData?.data])

  const boxesWithStatus = useMemo(() => {
    // Active alarm wins over presence: a box that fires while offline
    // still shows as 'high' / 'medium' so the operator triages it.
    // Otherwise, online state from the shared isBoxOnline helper —
    // same rule the dashboard hero uses, so totals always agree.
    const now = Date.now()
    return (measuringBoxesMaps ?? []).map((box) => {
      const id = Number(box.box_id)
      const alarmed = severityByBoxId.get(id)
      if (alarmed) return { ...box, status: alarmed as Status }
      const status: Status = isBoxOnline(box as any, now) ? 'ok' : 'offline'
      return { ...box, status }
    })
  }, [measuringBoxesMaps, severityByBoxId])

  const totalsByStatus = useMemo(() => {
    const t: Record<Status, number> = { high: 0, medium: 0, ok: 0, offline: 0 }
    for (const b of boxesWithStatus) t[b.status] += 1
    return t
  }, [boxesWithStatus])

  // Side-panel state — search + status + protocol filters. Default
  // status is "Todas" and default protocol is "all" so the operator
  // sees every box at a glance; chips narrow down per use case.
  // so the operator sees every box of the company at a glance; the
  // "Alarmadas" chip is one tap away for triage.
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'alarmed' | 'all' | Status>('all')
  const [page, setPage] = useState(1)

  const PAGE_SIZE = 100
  const STATUS_PRIORITY: Record<Status, number> = { high: 0, medium: 1, ok: 2, offline: 3 }

  // Reset to page 1 whenever filters/search change so the user isn't
  // stranded on a now-empty page.
  useEffect(() => { setPage(1) }, [search, statusFilter])

  const filteredList = useMemo(() => {
    const term = search.trim().toLowerCase()
    return boxesWithStatus
      .filter((b) => {
        if (statusFilter === 'alarmed' && !(b.status === 'high' || b.status === 'medium')) return false
        if (statusFilter !== 'alarmed' && statusFilter !== 'all' && b.status !== statusFilter) return false
        if (term) {
          const cmb = String(b.cmb_number ?? '').toLowerCase()
          const bt = String(b.bluetooth ?? '').toLowerCase()
          if (!cmb.includes(term) && !bt.includes(term)) return false
        }
        return true
      })
      .sort((a, b) => {
        // Primary: severity (high > medium > ok > offline) — same as
        // before so triage stays at the top.
        const sev = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status]
        if (sev !== 0) return sev
        // Secondary: geocoded boxes before ungeocoded. Ungeocoded
        // entries still appear (they count in totals) but slide to
        // the bottom so the operator sees actionable ones first.
        const aGeo = a.lat !== null && a.lat !== undefined ? 1 : 0
        const bGeo = b.lat !== null && b.lat !== undefined ? 1 : 0
        if (aGeo !== bGeo) return bGeo - aGeo
        // Tertiary: cmb_number ascending — stable order within group.
        return Number(a.cmb_number ?? 0) - Number(b.cmb_number ?? 0)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxesWithStatus, search, statusFilter])

  // Client-side pagination — full list lives in memory anyway (the
  // /measuring-box/:company_id/maps endpoint returns the whole roster),
  // so this just clips what we render. Avoids painting 1k+ DOM nodes
  // on huge companies; sidebar stays responsive.
  const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pagedList = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE
    return filteredList.slice(start, start + PAGE_SIZE)
  }, [filteredList, safePage])

  const [focusedCenter, setFocusedCenter] = useState<[number, number] | null>(null)
  const [activePopup, setActivePopup] = useState<number | null>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const markerRefs = useRef(new Map<number, L.Marker>())

  const handleSidebarClick = (boxId: number, lat: number, lng: number) => {
    setFocusedCenter([lat, lng])
    setActivePopup(boxId)
    setTimeout(() => markerRefs.current.get(boxId)?.openPopup(), 100)
  }

  if (isFetching && !measuringBoxesMaps?.length) {
    return (
      <div style={{ padding: TOKENS.spacing.lg, display: 'flex', alignItems: 'center', gap: TOKENS.spacing.sm }}>
        <span className='spinner-border spinner-border-sm' />
        <span style={{ fontSize: 14, color: TOKENS.color.muted }}>Carregando o mapa…</span>
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: fullscreen ? 0 : TOKENS.spacing.md,
        height: 'calc(100vh - 180px)',
        minHeight: 480,
      }}
    >
      {!fullscreen && (
        <aside style={{ width: 280, display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md, flexShrink: 0 }}>
          <PageCard>
            <SectionLabel>Status das caixas</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: TOKENS.spacing.sm, marginTop: TOKENS.spacing.md }}>
              <StatBlock value={totalsByStatus.high} label='Alta' tone='high' size='sm' />
              <StatBlock value={totalsByStatus.medium} label='Média' tone='medium' size='sm' />
              <StatBlock value={totalsByStatus.ok} label='OK' tone='low' size='sm' />
              <StatBlock value={totalsByStatus.offline} label='Offline' tone='neutral' size='sm' />
            </div>
          </PageCard>

          <PageCard style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <SectionLabel>
              Caixas {filteredList.length !== boxesWithStatus.length && <span style={{ color: TOKENS.color.text, fontWeight: 700 }}>({filteredList.length})</span>}
            </SectionLabel>

            <input
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='🔍 Buscar CMB ou Bluetooth'
              style={{
                marginTop: TOKENS.spacing.sm,
                border: `1px solid ${TOKENS.color.border}`,
                borderRadius: TOKENS.radius.chip,
                padding: '6px 10px',
                fontSize: 12,
                outline: 'none',
                color: TOKENS.color.text,
              }}
            />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: TOKENS.spacing.sm }}>
              {([
                { key: 'alarmed', label: 'Alarmadas' },
                { key: 'high', label: 'Alta' },
                { key: 'medium', label: 'Média' },
                { key: 'ok', label: 'OK' },
                { key: 'offline', label: 'Offline' },
                { key: 'all', label: 'Todas' },
              ] as { key: typeof statusFilter; label: string }[]).map((opt) => {
                const active = statusFilter === opt.key
                return (
                  <span
                    key={opt.key}
                    role='button'
                    onClick={() => setStatusFilter(opt.key)}
                    style={{
                      padding: '3px 9px',
                      borderRadius: TOKENS.radius.chip,
                      background: active ? TOKENS.color.action : TOKENS.color.surface,
                      color: active ? '#fff' : TOKENS.color.text,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </span>
                )
              })}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', marginTop: TOKENS.spacing.sm, marginRight: -8, paddingRight: 8 }}>
              {filteredList.length === 0 ? (
                <div style={{ fontSize: 12, color: TOKENS.color.muted, padding: '12px 0', textAlign: 'center' }}>
                  Nenhuma caixa encontrada.
                </div>
              ) : (
                pagedList.map((b) => {
                  const lat = parseFloat(String(b.lat))
                  const lng = parseFloat(String(b.long))
                  // Ungeocoded boxes still render in the list — they
                  // count in the company's totals — but tapping the
                  // row can't pan the map to nowhere, so we suppress
                  // the click and add a quiet visual hint.
                  const hasCoords = !Number.isNaN(lat) && !Number.isNaN(lng)
                  return (
                    <div
                      key={String(b.box_id)}
                      role={hasCoords ? 'button' : undefined}
                      onClick={hasCoords ? () => handleSidebarClick(Number(b.box_id), lat, lng) : undefined}
                      title={hasCoords ? undefined : 'Sem coordenadas — não aparece no mapa'}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 10px',
                        marginBottom: 4,
                        borderRadius: TOKENS.radius.chip,
                        background: TOKENS.color.surface,
                        cursor: hasCoords ? 'pointer' : 'default',
                        opacity: hasCoords ? 1 : 0.65,
                        borderLeft: `3px solid ${STATUS_COLOR[b.status]}`,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600, color: TOKENS.color.text, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        CMB {b.cmb_number}
                        {!hasCoords && (
                          <span style={{ fontSize: 9, fontWeight: 700, color: TOKENS.color.muted, letterSpacing: 0.4 }}>
                            (sem mapa)
                          </span>
                        )}
                      </span>
                      <span style={{
                        background: STATUS_COLOR[b.status],
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: TOKENS.radius.chip,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                      }}>
                        {STATUS_LABEL[b.status]}
                      </span>
                    </div>
                  )
                })
              )}
            </div>

            {/* Paginator — visible only when the filtered list spans
                more than one page. Click-targets are large enough to
                use from touch; the page counter stays compact. */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: TOKENS.spacing.sm,
                paddingTop: TOKENS.spacing.sm,
                borderTop: `1px solid ${TOKENS.color.border}`,
              }}>
                <button
                  type='button'
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  style={{
                    padding: '4px 10px',
                    borderRadius: TOKENS.radius.chip,
                    border: `1px solid ${TOKENS.color.border}`,
                    background: safePage <= 1 ? TOKENS.color.surface : TOKENS.color.card,
                    color: safePage <= 1 ? TOKENS.color.muted : TOKENS.color.text,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ‹ Anterior
                </button>
                <span style={{ fontSize: 10, fontWeight: 700, color: TOKENS.color.muted, letterSpacing: 0.4 }}>
                  {safePage} / {totalPages} · {filteredList.length} caixas
                </span>
                <button
                  type='button'
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  style={{
                    padding: '4px 10px',
                    borderRadius: TOKENS.radius.chip,
                    border: `1px solid ${TOKENS.color.border}`,
                    background: safePage >= totalPages ? TOKENS.color.surface : TOKENS.color.card,
                    color: safePage >= totalPages ? TOKENS.color.muted : TOKENS.color.text,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  Próxima ›
                </button>
              </div>
            )}
          </PageCard>
        </aside>
      )}

      <div
        style={{
          position: 'relative',
          flex: 1,
          borderRadius: TOKENS.radius.card,
          overflow: 'hidden',
          border: `1px solid ${TOKENS.color.border}`,
          minHeight: 0,
        }}
      >
        <MapContainer
          // Initial center / zoom is a Brazil-wide framing. The
          // FitToAllOnce effect (added below) reframes to the actual
          // fleet bounds as soon as the box data lands, so this is
          // really just the no-data fallback.
          center={focusedCenter ?? BRAZIL_CENTER}
          zoom={focusedCenter ? 15 : BRAZIL_ZOOM}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <LayersControl position='topleft'>
            <LayersControl.BaseLayer checked name='Mapa'>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='Satélite'>
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='Híbrido'>
              {/* BaseLayer expects a SINGLE layer child — wrap the two
                  TileLayers in a LayerGroup so they paint together but
                  the picker only shows one Híbrido entry. */}
              <LayerGroup>
                <TileLayer
                  attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
                  url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                  maxZoom={19}
                />
                <TileLayer
                  attribution='Reference &copy; Esri'
                  url='https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
                  maxZoom={19}
                  opacity={0.9}
                />
              </LayerGroup>
            </LayersControl.BaseLayer>
          </LayersControl>
          <Recenter position={focusedCenter} zoom={16} />
          <ResetToBrazilOnKey resetKey={companyId} />
          <InvalidateOnResize trigger={fullscreen} />
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={buildClusterIcon}
            // Tightish radius so visually-close boxes group without
            // collapsing the whole city into one circle.
            maxClusterRadius={50}
            // Spider out at the highest zoom so individual markers in
            // dense projects (multiple boxes per street) become reachable.
            spiderfyOnMaxZoom
            showCoverageOnHover={false}
          >
          {boxesWithStatus.map((box) => {
            const lat = parseFloat(String(box.lat))
            const lng = parseFloat(String(box.long))
            if (Number.isNaN(lat) || Number.isNaN(lng)) return null
            const boxId = Number(box.box_id)
            return (
              <Marker
                key={String(box.box_id)}
                position={[lat, lng]}
                icon={buildMarkerIcon(box.cmb_number, box.status)}
                // Status piggybacks on `title` so the cluster icon
                // builder can read it without us having to subclass the
                // Marker. `title` also shows on browser hover, which is
                // a useful side-effect.
                title={box.status}
                eventHandlers={{ click: () => setActivePopup(boxId) }}
                ref={(m) => { if (m) markerRefs.current.set(boxId, m as unknown as L.Marker) }}
              >
                {activePopup === boxId && (
                  <Popup
                    position={[lat, lng]}
                    eventHandlers={{ remove: () => setActivePopup(null) }}
                  >
                    <BoxPopup
                      box={{
                        id: Number(box.box_id) as unknown as ID,
                        cmb_number: box.cmb_number,
                        bluetooth: box.bluetooth,
                        project_name: box.project_name,
                        model_type: box.model_type,
                        lat,
                        lng,
                        status: box.status,
                      }}
                      onConsult={() => {
                        setReadOnly && setReadOnly(true)
                        setItemIdForUpdate(Number(box.box_id) as unknown as ID)
                      }}
                    />
                  </Popup>
                )}
              </Marker>
            )
          })}
          </MarkerClusterGroup>
        </MapContainer>
        <button
          type='button'
          onClick={() => setFullscreen((v) => !v)}
          title={fullscreen ? 'Recolher mapa' : 'Ampliar mapa'}
          style={{
            position: 'absolute',
            top: TOKENS.spacing.md,
            right: TOKENS.spacing.md,
            zIndex: 1000,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: TOKENS.color.card,
            border: `1px solid ${TOKENS.color.border}`,
            borderRadius: TOKENS.radius.card,
            padding: '6px 10px',
            fontSize: 12,
            fontWeight: 600,
            color: TOKENS.color.text,
            cursor: 'pointer',
            boxShadow: TOKENS.shadow.card,
          }}
        >
          {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          <span>{fullscreen ? 'Recolher' : 'Ampliar'}</span>
        </button>
      </div>
      {itemIdForUpdate !== undefined && <EditModal />}
    </div>
  )
}

type PopupBox = {
  id: ID
  cmb_number: number
  bluetooth: string
  project_name: string
  model_type: number
  lat: number
  lng: number
  status: Status
}

const BoxPopup: FC<{ box: PopupBox; onConsult: () => void }> = ({ box, onConsult }) => {
  const color = STATUS_COLOR[box.status]
  const modelLabel = MeasuringBoxModelsList.find((m) => m.id === box.model_type)?.name
    ?? (box.model_type === MeasuringBoxModels.bluetooth_com_barreira
      ? 'Bluetooth com barreira'
      : box.model_type === MeasuringBoxModels.bluetooth_sem_barreira
      ? 'Bluetooth sem barreira'
      : 'Não identificado')
  return (
    <div style={{ minWidth: 220, fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: TOKENS.spacing.sm, marginBottom: TOKENS.spacing.md }}>
        <span style={{
          background: color, color: '#fff',
          padding: '2px 8px', borderRadius: TOKENS.radius.chip,
          fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
        }}>
          {STATUS_LABEL[box.status]}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: TOKENS.color.text }}>
          CMB {box.cmb_number}
        </span>
      </div>
      <div style={{ fontSize: 12, color: TOKENS.color.muted, lineHeight: 1.6 }}>
        <div><strong style={{ color: TOKENS.color.text }}>Projeto:</strong> {box.project_name || '—'}</div>
        <div><strong style={{ color: TOKENS.color.text }}>Bluetooth:</strong> {box.bluetooth}</div>
        <div><strong style={{ color: TOKENS.color.text }}>Modelo:</strong> {modelLabel}</div>
        <div style={{ marginTop: 4 }}>
          {box.lat.toFixed(6)}, {box.lng.toFixed(6)}
        </div>
      </div>
      <button
        type='button'
        onClick={onConsult}
        style={{
          marginTop: TOKENS.spacing.md,
          width: '100%',
          border: 'none',
          background: TOKENS.color.action,
          color: '#fff',
          padding: '8px 12px',
          borderRadius: TOKENS.radius.chip,
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Consultar detalhes
      </button>
    </div>
  )
}

export const LeafletMap: FC = () => (
  <SensorTransportProvider transport='websocket'>
    <LeafletMapInner />
  </SensorTransportProvider>
)
